/**
 * GFG-273 (Campaigns tab, GFG Admin). One function per `/admin/campaigns` endpoint, following
 * apps/gfgf-ui/src/api/sendQueueCampaigns.ts: query params are snake_case, arrays are comma-joined,
 * empty/absent values are omitted from the querystring; JSON response fields are camelCase. Only
 * values from the contract's vocabularies are ever emitted — the server answers an unknown
 * querystring with a 400, which would be a bug here rather than a user-reachable state.
 */
import type {
  AdminCampaignImpersonationResult,
  AdminCampaignStatusResult,
  AdminCampaignsListParams,
  AdminCampaignsListResult,
} from "@/interfaces/adminCampaigns";
import { apiClient } from "@/lib/apiClient";

/** GET /admin/campaigns -- paginated, filterable, sortable list of every client's campaigns. */
export async function fetchAdminCampaigns(
  params?: AdminCampaignsListParams,
): Promise<AdminCampaignsListResult> {
  const query: Record<string, string | number> = {};
  if (params?.search) query.search = params.search;
  if (params?.organization_id?.length) query.organization_id = params.organization_id.join(",");
  if (params?.status?.length) query.status = params.status.join(",");
  if (params?.campaign_type?.length) query.campaign_type = params.campaign_type.join(",");
  if (params?.send_method?.length) query.send_method = params.send_method.join(",");
  if (params?.scheduled_from) query.scheduled_from = params.scheduled_from;
  if (params?.scheduled_to) query.scheduled_to = params.scheduled_to;
  if (params?.expiry_from) query.expiry_from = params.expiry_from;
  if (params?.expiry_to) query.expiry_to = params.expiry_to;
  if (params?.sortBy) query.sortBy = params.sortBy;
  if (params?.sortDir) query.sortDir = params.sortDir;
  if (params?.page && params.page > 1) query.page = params.page;
  if (params?.limit) query.limit = params.limit;
  if (params?.includeFilterOptions) query.includeFilterOptions = "true";

  const { data } = await apiClient.get<AdminCampaignsListResult>("/admin/campaigns", {
    params: query,
  });
  return data;
}

/** POST /admin/campaigns/:id/pause */
export async function pauseAdminCampaign(id: number): Promise<AdminCampaignStatusResult["data"]> {
  const { data } = await apiClient.post<AdminCampaignStatusResult>(`/admin/campaigns/${id}/pause`);
  return data.data;
}

/** POST /admin/campaigns/:id/resume -- the server decides which status a resume restores. */
export async function resumeAdminCampaign(id: number): Promise<AdminCampaignStatusResult["data"]> {
  const { data } = await apiClient.post<AdminCampaignStatusResult>(`/admin/campaigns/${id}/resume`);
  return data.data;
}

/** POST /admin/campaigns/:id/archive */
export async function archiveAdminCampaign(id: number): Promise<AdminCampaignStatusResult["data"]> {
  const { data } = await apiClient.post<AdminCampaignStatusResult>(
    `/admin/campaigns/${id}/archive`,
  );
  return data.data;
}

/** POST /admin/campaigns/:id/<cancel|restore|disable|enable> -- same envelope as pause/resume. */
export async function postAdminCampaignStatus(
  id: number,
  action: "cancel" | "restore" | "disable" | "enable",
): Promise<AdminCampaignStatusResult["data"]> {
  const { data } = await apiClient.post<AdminCampaignStatusResult>(
    `/admin/campaigns/${id}/${action}`,
  );
  return data.data;
}

/** POST /admin/campaigns/:id/impersonate -- the actor token for the campaign's creator. */
export async function impersonateAdminCampaign(
  id: number,
): Promise<AdminCampaignImpersonationResult["data"]> {
  const { data } = await apiClient.post<AdminCampaignImpersonationResult>(
    `/admin/campaigns/${id}/impersonate`,
  );
  return data.data;
}
